import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "@/hooks/useApi";
import { apiPost } from "@/lib/api";
import { Skeleton } from "@/components/base/Skeleton";
import Avatar from "@/components/base/Avatar";
import type { AttendData } from "@/lib/types";
import { useAuth } from "@/components/feature/AuthProvider";
import { useToast } from "@/components/feature/ToastProvider";

const MAX_AVATARS = 12;

// 메인 출근도장 섹션 (오늘의 도장 + 오늘 출근 회원)
export default function AttendSection() {
  const { requireLogin } = useAuth();
  const { toast } = useToast();
  const { data, loading, error, reload } = useApi<AttendData>("attend.php");
  const [checking, setChecking] = useState(false);

  const todayCnt = data?.today_cnt ?? 0;
  const myToday = data?.my_today === 1;
  const list = data?.today_list ?? [];
  const shown = list.slice(0, MAX_AVATARS);
  const restCount = Math.max(0, list.length - MAX_AVATARS);

  const handleCheck = useCallback(async () => {
    if (myToday || checking) return;
    if (!requireLogin()) return;
    setChecking(true);
    try {
      const res = await apiPost<{ today_cnt: number }>("attend_check.php", {});
      toast(res.msg || "출근도장을 찍었습니다.", "success");
      reload();
    } catch (e) {
      toast(e instanceof Error ? e.message : "도장을 찍지 못했습니다.", "error");
    } finally {
      setChecking(false);
    }
  }, [myToday, checking, requireLogin, toast, reload]);

  return (
    <section className="w-full px-4 md:px-6 py-14 md:py-16">
      <div className="mx-auto max-w-6xl">
        <div className="overflow-hidden rounded-2xl bg-primary-700">
          <div className="flex items-center justify-between gap-4 px-5 pt-5 md:px-8 md:pt-7">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-background-50">
              출근도장
            </h2>
            <Link
              to="/attend"
              className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-background-50/30 bg-background-50/10 px-4 py-2 text-sm font-semibold text-background-50 hover:bg-background-50/20 transition-colors"
            >
              출근도장 가기
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>

          <div className="flex flex-col items-center px-5 pb-8 pt-6 md:px-8 md:pb-10 md:pt-8">
            {loading ? (
              <div className="flex flex-col items-center gap-4 py-4">
                <Skeleton className="h-28 w-28 rounded-full bg-background-50/20" />
                <Skeleton className="h-4 w-48 bg-background-50/20" />
                <div className="flex gap-3">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <Skeleton key={i} className="h-11 w-11 rounded-full bg-background-50/20" />
                  ))}
                </div>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center gap-3 py-6 text-center">
                <p className="text-sm text-background-100/90">일시적으로 불러오지 못했습니다.</p>
                <button
                  type="button"
                  onClick={reload}
                  className="whitespace-nowrap rounded-full bg-background-50 px-4 py-2 text-xs font-semibold text-primary-700 hover:bg-background-100 transition-colors"
                >
                  다시 시도
                </button>
              </div>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleCheck}
                  disabled={myToday || checking}
                  className={`flex h-28 w-28 flex-col items-center justify-center gap-1 rounded-full text-center font-bold transition-colors md:h-32 md:w-32 ${
                    myToday
                      ? "cursor-not-allowed bg-background-50/20 text-background-50"
                      : "bg-secondary-500 text-primary-950 hover:bg-secondary-600"
                  }`}
                >
                  <i
                    className={
                      myToday ? "ri-checkbox-circle-fill text-3xl" : "ri-fingerprint-line text-3xl"
                    }
                    aria-hidden="true"
                  ></i>
                  <span className="text-sm">
                    {myToday ? "오늘 도장 완료" : checking ? "찍는 중…" : "도장찍기"}
                  </span>
                </button>

                <p className="mt-5 text-sm font-medium text-background-100/90">
                  오늘 <span className="font-bold text-background-50">{todayCnt}명</span>이 도장을
                  찍었습니다
                </p>

                {shown.length > 0 && (
                  <div className="mt-5 flex flex-wrap items-center justify-center gap-4">
                    {shown.map((p, i) => (
                      <div key={`${p.name}-${i}`} className="flex w-14 flex-col items-center gap-1.5">
                        <Avatar name={p.name || "회원"} photo={p.photo} size={44} />
                        <span className="w-full truncate text-center text-[11px] text-background-100/90">
                          {[p.name, p.aho].filter(Boolean).join(" ") || "회원"}
                        </span>
                      </div>
                    ))}
                    {restCount > 0 && (
                      <div className="flex w-14 flex-col items-center gap-1.5">
                        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-background-50/20 text-xs font-bold text-background-50">
                          +{restCount}
                        </span>
                        <span className="text-[11px] text-background-100/90">더보기</span>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}